import React from "react";
import { Link } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import methodModel from "../../methods/methods";

export default function AboutUS() {
  const helpItems = [
    "Select the right equipment for your application",
    "Locate replacement parts and hard-to-find products",
    "Confirm product and component compatibility",
    "Source equipment from multiple manufacturers",
    "Consolidate orders into fewer, more economical shipments",
    "Coordinate delivery throughout the Caribbean",
    "Resolve questions and problems after the sale",
  ];

  return (
    <div>
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px]">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0D4B82] via-[#115B9D] to-[#116DAE] text-white py-14 md:py-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_50%)] pointer-events-none" />
          <div className="max-w-[1200px] mx-auto px-5 relative z-10 text-center">
            <span className="inline-block bg-white/15 backdrop-blur-md text-blue-100 text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4 border border-white/20">
              About Charles S. Smith & Associates
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight max-w-4xl mx-auto">
              Beverage Equipment Made Easier Across the Caribbean
            </h1>
            <p className="mt-5 text-blue-100 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
              Buying beverage-dispensing equipment shouldn’t mean searching through dozens of suppliers, guessing whether parts are compatible, or coordinating multiple shipments on your own.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 bg-white/20 backdrop-blur-md text-white font-medium text-sm sm:text-base px-6 py-2.5 rounded-full border border-white/25 shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Charles S. Smith & Associates makes the process easier.</span>
            </div>
          </div>
        </section>

        {/* Main Content Area */}
        <section className="max-w-[1200px] mx-auto px-5 py-12 md:py-16 space-y-12 md:space-y-16">
          {/* Wholesale & Purpose Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-7 md:p-8 shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition duration-300">
              <div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#115B9D] flex items-center justify-center mb-5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Wholesale Caribbean Distributor</h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                  We are a wholesale distributor serving businesses throughout the Caribbean with beverage-dispensing equipment, replacement parts, water-filtration systems, coolers, ice makers, post-mix systems, juice dispensers, beer equipment, and related supplies.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-7 md:p-8 shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition duration-300">
              <div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#115B9D] flex items-center justify-center mb-5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Beyond Order Fulfillment</h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                  Our role goes far beyond filling an order. We help you identify the right products, source difficult-to-find items, confirm compatibility, consolidate purchases, coordinate shipping, and provide dependable support when questions arise.
                </p>
              </div>
            </div>
          </div>

          {/* One Trusted Partner Section */}
          <div className="bg-gradient-to-b from-gray-50 to-white rounded-3xl p-7 md:p-10 border border-gray-200/80 shadow-sm space-y-8">
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-[#116DAE] uppercase tracking-widest block mb-2">Our Value</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-snug">
                One Trusted Partner from Sourcing to Support
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed text-sm md:text-base">
                The most expensive equipment problem is often not the purchase price—it is ordering the wrong product, discovering that parts do not work together, paying unnecessary freight costs, or experiencing downtime while searching for a replacement.
              </p>
              <p className="mt-3 text-gray-700 font-medium text-sm md:text-base">
                Our experienced team helps prevent those problems by managing the details from the beginning.
              </p>
            </div>

            {/* We can help you list */}
            <div>
              <h4 className="text-sm uppercase tracking-wider font-semibold text-gray-500 mb-4">We can help you:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {helpItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100 shadow-2xs">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-[#115B9D] flex items-center justify-center shrink-0 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm font-medium leading-snug">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#115B9D]/5 rounded-2xl p-5 border border-[#115B9D]/15 text-center sm:text-left flex flex-col sm:flex-row items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#115B9D] text-white flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 text-sm sm:text-base font-medium leading-relaxed">
                Whether you need one replacement part or equipment for an entire beverage-dispensing system, you receive the same personal attention and practical support.
              </p>
            </div>
          </div>

          {/* Two Columns: Serving Customers Since 1976 & More Than a Distributor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Serving Customers Since 1976 */}
            <div className="bg-white rounded-3xl p-7 md:p-9 border border-gray-100 shadow-sm flex flex-col justify-between space-y-5">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 text-xs font-bold text-[#116DAE] uppercase tracking-wider bg-blue-50 px-3.5 py-1.5 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#116DAE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Established 1976
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Serving Customers Since 1976</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  Charles S. Smith & Associates was founded in 1976 with a commitment to knowledgeable, dependable service. That commitment continues to guide us today.
                </p>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  Over the years, products, technology, and supply chains have changed—but our approach has remained simple: listen carefully, understand what the customer is trying to accomplish, and find the best practical solution.
                </p>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  Our experience helps us recognize potential problems before they become costly ones. We understand the importance of reliable equipment, accurate orders, efficient shipping, and responsive communication—especially when serving customers across the Caribbean.
                </p>
              </div>
            </div>

            {/* More Than a Distributor */}
            <div className="bg-white rounded-3xl p-7 md:p-9 border border-gray-100 shadow-sm flex flex-col justify-between space-y-5">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 text-xs font-bold text-[#116DAE] uppercase tracking-wider bg-blue-50 px-3.5 py-1.5 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#116DAE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Our Philosophy
                </div>
                <h3 className="text-2xl font-bold text-gray-900">More Than a Distributor</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  We believe a good distributor should make your job easier.
                </p>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  That means answering questions honestly, recommending products that fit your needs, keeping you informed, and being available when you need help. It also means looking beyond individual products to consider compatibility, reliability, shipping costs, and the long-term needs of your operation.
                </p>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-gray-800 text-sm md:text-base font-medium leading-relaxed italic">
                  "When you work with Charles S. Smith & Associates, you gain a resource you can call—not simply another company that sells equipment."
                </div>
              </div>
            </div>
          </div>

          {/* CTA Banner: Let Us Help You Find the Right Solution */}
          <div className="bg-gradient-to-br from-[#115B9D] to-[#116DAE] rounded-3xl p-8 sm:p-12 text-white shadow-xl relative overflow-hidden text-center sm:text-left">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-60 h-60 bg-white/10 rounded-full blur-2xl pointer-events-none" />
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="max-w-2xl space-y-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight">
                  Let Us Help You Find the Right Solution
                </h2>
                <p className="text-blue-100 text-sm sm:text-base leading-relaxed">
                  Tell us what you need, what equipment you are working with, or what problem you are trying to solve. Our team will help you identify your options and prepare a quote at no cost.
                </p>
                <p className="text-amber-300 font-semibold text-base sm:text-lg">
                  Let’s make your next equipment purchase simpler.
                </p>
              </div>

              <div className="shrink-0">
                <Link
                  to="/contact/us"
                  className="inline-flex items-center gap-2 bg-white text-[#115B9D] hover:bg-blue-50 font-bold px-7 py-3.5 rounded-full shadow-lg transition duration-300 transform hover:scale-[1.04]"
                >
                  <span>Contact Our Team</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {methodModel.TokenChecker() && <Footer />}
    </div>
  );
}